import { type QConfigTableData } from '@exam-management/common/types';
import { QConfigStructureCategory } from '@exam-management/graphql';
import { useCallback, useMemo } from 'react';

import { type QConfigStructure } from '../../types';
import { useGetLevelItemsWithLevel } from '../use-get-level-items-with-level';

function getAllSubTopicsByParentId(
  parentId: string,
  structures: QConfigStructure[],
): QConfigStructure[] {
  return structures.filter(
    (structure) =>
      structure.parentId === parentId &&
      structure.category === QConfigStructureCategory.AllSubTopic,
  );
}

export interface UseQuestionConditionsDataInput {
  chapterData: QConfigStructure;
  structures: QConfigStructure[];
  questionTypeRow?: QConfigTableData;
}

export function useQuestionConditionsData(input: Readonly<UseQuestionConditionsDataInput>) {
  const { chapterData, structures, questionTypeRow } = input;

  const { levelItemsWithLevel, isLoading } = useGetLevelItemsWithLevel({
    chapterData,
    structures,
  });

  const allSubTopicParentListByChapter = useMemo(() => {
    const questionTypeStructuresByChapterId = structures.filter(
      (structure) => structure.parentId === chapterData.id,
    );
    if (!questionTypeStructuresByChapterId) {
      return [];
    }

    const allSubTopicByChapter = Array.from(
      new Set(
        questionTypeStructuresByChapterId.flatMap((questionTypeStructure) => {
          const topics = structures.filter(
            (structure) => structure.parentId === questionTypeStructure.id,
          );

          const subTopics = topics.flatMap((topic) =>
            getAllSubTopicsByParentId(topic.id, structures),
          );

          return subTopics;
        }),
      ),
    );

    return allSubTopicByChapter
      .map((subTopic) => {
        const parent = structures.find((structure) => structure.id === subTopic.parentId);
        return parent;
      })
      .filter((parent) => parent !== undefined);
  }, [structures, chapterData]);

  const questionTypeFamilyIds = useMemo(() => {
    if (!questionTypeRow) return [];

    const childrenIds = structures
      .filter((structure) => structure.parentId === questionTypeRow.id)
      .map((item) => item.id);
    const grandchildrenIds = childrenIds
      .flatMap((childId) => structures.filter((structure) => structure.parentId === childId))
      .map((item) => item.id);

    // REMARK: include self, children, grandchildren
    const family = structures.filter((structure) => {
      const isSelf = structure.id === questionTypeRow.id;
      const isChild = childrenIds.includes(structure.id);
      const isGrandchild = grandchildrenIds.includes(structure.id);

      return isSelf || isChild || isGrandchild;
    });

    return family.map((item) => item.id);
  }, [structures, questionTypeRow]);

  const checkExistOnStructures = useCallback(
    (subTopicLevelItemId: string) => {
      const existOnStructure = structures.find((structure) => {
        const matchRefId = structure.refId === subTopicLevelItemId;
        const notInQuestionTypeFamily = !questionTypeFamilyIds.includes(structure.id);

        return matchRefId && notInQuestionTypeFamily;
      });

      if (existOnStructure) {
        return true;
      } else if (allSubTopicParentListByChapter.length) {
        // REMARK: handle check all sub topic
        const subTopicLevelItem = levelItemsWithLevel.find(
          (item) => item.id === subTopicLevelItemId,
        );

        if (subTopicLevelItem?.parentLevelItemId === chapterData?.refId) {
          return true;
        }

        const subTopicParentStructure = allSubTopicParentListByChapter.find((topic) => {
          const matchRefId = topic.refId === subTopicLevelItem?.parentLevelItemId;
          const notInQuestionTypeFamily = !questionTypeFamilyIds.includes(topic.id);

          return matchRefId && notInQuestionTypeFamily;
        });

        const foundSubtopicParent = !!subTopicParentStructure;
        return foundSubtopicParent;
      }

      return false;
    },
    [
      structures,
      questionTypeFamilyIds,
      allSubTopicParentListByChapter,
      levelItemsWithLevel,
      chapterData,
    ],
  );

  return {
    levelItemsWithLevel,
    isLoading,
    checkExistOnStructures,
  };
}
